/**
 * @fileoverview Appeal Timeline Component
 * @module AppealTimeline
 * 
 * Business Context:
 * - Anchors students in the Texas property tax appeal calendar
 * - Shows current phase and what to do next
 * - Handles corner cases like pre-notice signups and scheduled meetings
 * 
 * Visual Design:
 * - Horizontal phase bar showing progress through appeal process
 * - Calendar strip showing key Texas property tax dates
 * - Context message with actionable guidance
 * - Urgency indicators for deadlines
 */

import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList,
  FileText,
  Search,
  Calendar,
  Trophy,
  ChevronRight,
  AlertCircle,
  Clock,
  CheckCircle2,
  MapPin,
} from 'lucide-react';
import {
  AppealPhase,
  AppealTimelineData,
  calculateCurrentPhase,
  calculateProtestDeadline,
  getAllPhases,
  getPhaseInfo,
  isPhaseComplete,
  isCurrentPhase,
  isUpcomingPhase,
  PhaseResult,
  formatShortDate,
} from '../../utils/appealTimeline';

// ============================================
// CALENDAR STRIP SUB-COMPONENT
// ============================================

interface CalendarStripProps {
  noticeDate: Date | null;
  hearingDate: Date | null;
  protestFiled: boolean;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep'];

/**
 * Calculate the position (0-100%) for a given date within the calendar year
 */
function getDatePosition(date: Date): number {
  const startOfYear = new Date(date.getFullYear(), 0, 1);
  const endOfCalendar = new Date(date.getFullYear(), 8, 30); // Through September
  const totalDays = (endOfCalendar.getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24);
  const dayOfYear = (date.getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24);
  return Math.min(100, Math.max(0, (dayOfYear / totalDays) * 100));
}

/**
 * Get position for a month/day combination
 */
function getMonthDayPosition(month: number, day: number): number {
  const year = new Date().getFullYear();
  const date = new Date(year, month, day);
  return getDatePosition(date);
}

const CalendarStrip: React.FC<CalendarStripProps> = ({ noticeDate, hearingDate, protestFiled }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const today = new Date();
  const todayPosition = getDatePosition(today);
  const currentYear = today.getFullYear();
  
  // Calculate user-specific dates
  const userDeadline = noticeDate ? calculateProtestDeadline(noticeDate) : null;
  const deadlinePosition = userDeadline ? getDatePosition(userDeadline) : getMonthDayPosition(4, 15);
  const hearingPosition = hearingDate ? getDatePosition(hearingDate) : null;
  
  // Auto-scroll to today on mount (mobile)
  useEffect(() => {
    if (scrollRef.current) {
      const scrollWidth = scrollRef.current.scrollWidth;
      const clientWidth = scrollRef.current.clientWidth;
      const scrollTo = (scrollWidth * todayPosition / 100) - (clientWidth / 2);
      scrollRef.current.scrollTo({ left: Math.max(0, scrollTo), behavior: 'smooth' });
    }
  }, [todayPosition]);

  return (
    <div className="mt-4 pt-4 border-t border-slate-100">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
          {currentYear} Tax Calendar
        </span>
        <span className="text-[11px] text-slate-400">
          Today: {formatShortDate(today)}
        </span>
      </div>
      
      {/* Calendar Strip Container */}
      <div 
        ref={scrollRef}
        className="overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent -mx-1 px-1"
      >
        <div className="relative min-w-[600px] h-[76px]">
          {/* Month Grid Background */}
          <div className="absolute inset-0 flex">
            {MONTHS.map((month) => (
              <div 
                key={month} 
                className="flex-1 border-l border-slate-100 first:border-l-0"
              >
                <span className="text-[10px] font-medium text-slate-400 pl-1.5 pt-1 block">
                  {month}
                </span>
              </div>
            ))}
          </div>
          
          {/* Period Bands and Markers */}
          <div className="absolute inset-0">
            
            {/* Notice Season Band - Apr to mid-May */}
            <div 
              className="absolute top-6 h-3.5 bg-slate-200/70 rounded-sm"
              style={{ 
                left: `${getMonthDayPosition(3, 1)}%`, 
                width: `${getMonthDayPosition(4, 15) - getMonthDayPosition(3, 1)}%` 
              }}
            />
            <div 
              className="absolute top-[42px]"
              style={{ left: `${getMonthDayPosition(3, 20)}%`, transform: 'translateX(-50%)' }}
            >
              <span className="text-[9px] font-medium text-slate-500 whitespace-nowrap">
                Notice Season
              </span>
            </div>
            
            {/* Meet with Assessor Band - May 15 to Jul (more prominent) */}
            <div 
              className="absolute top-6 h-3.5 bg-teal-200/80 rounded-sm border-l-2 border-teal-400"
              style={{ 
                left: `${getMonthDayPosition(4, 16)}%`, 
                width: `${getMonthDayPosition(6, 31) - getMonthDayPosition(4, 16)}%` 
              }}
            />
            <div 
              className="absolute top-[42px]"
              style={{ left: `${getMonthDayPosition(5, 20)}%`, transform: 'translateX(-50%)' }}
            >
              <span className="text-[9px] font-semibold text-teal-700 whitespace-nowrap">
                Meet with Assessor
              </span>
            </div>
            
            {/* FILING DEADLINE - Clear but refined marker */}
            <div 
              className="absolute top-4 flex flex-col items-center z-30"
              style={{ 
                left: `${deadlinePosition}%`, 
                transform: 'translateX(-50%)' 
              }}
            >
              {/* Vertical line */}
              <div className={`w-0.5 h-10 ${protestFiled ? 'bg-emerald-400' : 'bg-red-400'}`} />
              {/* Deadline label */}
              <div className={`-mt-0.5 px-2 py-0.5 rounded text-[9px] font-semibold whitespace-nowrap ${
                protestFiled 
                  ? 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200' 
                  : 'bg-red-50 text-red-700 ring-1 ring-red-200'
              }`}>
                {protestFiled ? 'FILED ✓' : 'DEADLINE'}
              </div>
            </div>
            
            {/* User's Meeting Date (if set) */}
            {hearingDate && hearingPosition !== null && (
              <div 
                className="absolute top-5 flex flex-col items-center z-20"
                style={{ left: `${hearingPosition}%`, transform: 'translateX(-50%)' }}
              >
                <div className="w-2.5 h-2.5 bg-teal-500 rounded-full ring-2 ring-white" />
                <div className="mt-1 px-1.5 py-0.5 bg-teal-600 text-white rounded text-[8px] font-semibold whitespace-nowrap">
                  Meeting
                </div>
              </div>
            )}
            
            {/* TODAY Marker - Clear with brand color */}
            <div 
              className="absolute top-4 flex flex-col items-center z-10"
              style={{ left: `${todayPosition}%`, transform: 'translateX(-50%)' }}
            >
              <div className="w-2.5 h-2.5 bg-teal-500 rounded-full ring-[3px] ring-teal-200" />
              <div className="w-0.5 h-8 bg-teal-400" />
              <span className="px-1.5 py-0.5 bg-teal-500 text-white rounded text-[8px] font-semibold whitespace-nowrap">
                Today
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Legend - Readable with refined styling */}
      <div className="flex flex-wrap items-center gap-5 mt-2.5 text-[11px] text-slate-500">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-2 bg-slate-200 rounded-sm" />
          <span>Notice Season</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className={`w-3 h-2 rounded-sm ${protestFiled ? 'bg-emerald-300' : 'bg-red-300'}`} />
          <span>Filing Deadline</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-2 bg-teal-300 rounded-sm border-l-2 border-teal-500" />
          <span>Meeting Period</span>
        </div>
      </div>
    </div>
  );
};

interface AppealTimelineProps {
  data: AppealTimelineData;
}

// Phase icons mapping
const phaseIcons: Record<AppealPhase, React.ElementType> = {
  prepare: ClipboardList,
  file: FileText,
  build_case: Search,
  hearing: Calendar,
  results: Trophy,
};

export const AppealTimeline: React.FC<AppealTimelineProps> = ({ data }) => {
  const navigate = useNavigate();
  const phaseResult = calculateCurrentPhase(data);
  const allPhases = getAllPhases();

  const handleActionClick = () => {
    if (phaseResult.actionRoute) {
      navigate(phaseResult.actionRoute);
    }
  };

  // Get urgency styling - refined for cohesive slate/teal palette
  const getUrgencyStyles = (urgency: PhaseResult['urgency']) => {
    switch (urgency) {
      case 'urgent':
        return {
          container: 'bg-white border-slate-200',
          accent: 'bg-red-500',
          accentLight: 'bg-red-50',
          badge: 'bg-red-50 text-red-600 ring-1 ring-red-100',
          button: 'bg-red-600 hover:bg-red-700',
          icon: 'text-red-500',
          progressLine: 'bg-red-500',
        };
      case 'attention':
        return {
          container: 'bg-white border-slate-200',
          accent: 'bg-teal-500',
          accentLight: 'bg-teal-50',
          badge: 'bg-teal-50 text-teal-600 ring-1 ring-teal-100',
          button: 'bg-teal-600 hover:bg-teal-700',
          icon: 'text-teal-500',
          progressLine: 'bg-teal-500',
        };
      default:
        return {
          container: 'bg-white border-slate-200',
          accent: 'bg-teal-500',
          accentLight: 'bg-teal-50',
          badge: 'bg-teal-50 text-teal-600 ring-1 ring-teal-100',
          button: 'bg-slate-900 hover:bg-slate-800',
          icon: 'text-teal-500',
          progressLine: 'bg-teal-500',
        };
    }
  };

  const styles = getUrgencyStyles(phaseResult.urgency);

  return (
    <div className={`rounded-xl shadow-sm border overflow-hidden mb-6 ${styles.container}`}>
      {/* Top accent bar */}
      <div className={`h-0.5 ${styles.accent}`} />
      
      <div className="p-4 sm:p-5">
        {/* Compact Phase Progress Bar - Desktop */}
        <div className="hidden sm:block mb-4">
          <div className="flex items-center justify-between relative">
            {/* Progress line */}
            <div className="absolute top-4 left-[10%] right-[10%] h-[2px] bg-slate-100" />
            <div 
              className={`absolute top-4 left-[10%] h-[2px] ${styles.progressLine} transition-all duration-500`}
              style={{ 
                width: `${(allPhases.indexOf(phaseResult.phase) / (allPhases.length - 1)) * 80}%` 
              }}
            />
            
            {/* Phase nodes - more compact */}
            {allPhases.map((phase) => {
              const Icon = phaseIcons[phase];
              const info = getPhaseInfo(phase);
              const isComplete = isPhaseComplete(phase, phaseResult.phase);
              const isCurrent = isCurrentPhase(phase, phaseResult.phase);
              
              return (
                <div 
                  key={phase} 
                  className="flex flex-col items-center relative z-10"
                  style={{ width: `${100 / allPhases.length}%` }}
                >
                  <div 
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                      isComplete 
                        ? 'bg-emerald-500 text-white' 
                        : isCurrent 
                          ? `${phaseResult.urgency === 'urgent' ? 'bg-red-500' : 'bg-teal-500'} text-white ring-2 ring-offset-1 ${phaseResult.urgency === 'urgent' ? 'ring-red-200' : 'ring-teal-200'}`
                          : 'bg-slate-100 text-slate-400'
                    }`}
                  >
                    {isComplete ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : (
                      <Icon className="w-4 h-4" />
                    )}
                  </div>
                  <span className={`mt-1.5 text-[11px] font-medium text-center ${
                    isCurrent 
                      ? 'text-slate-700' 
                      : isComplete 
                        ? 'text-emerald-600' 
                        : 'text-slate-400'
                  }`}>
                    {info.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Phase Progress - Mobile (pill style) */}
        <div className="sm:hidden mb-3">
          <div className="flex items-center gap-1 overflow-x-auto pb-1 -mx-1 px-1">
            {allPhases.map((phase, index) => {
              const isComplete = isPhaseComplete(phase, phaseResult.phase);
              const isCurrent = isCurrentPhase(phase, phaseResult.phase);
              const info = getPhaseInfo(phase);
              
              return (
                <React.Fragment key={phase}>
                  <div 
                    className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                      isComplete 
                        ? 'bg-emerald-50 text-emerald-600' 
                        : isCurrent 
                          ? `${styles.badge}`
                          : 'bg-slate-50 text-slate-400'
                    }`}
                  >
                    {isComplete && '✓ '}{info.label}
                  </div>
                  {index < allPhases.length - 1 && (
                    <ChevronRight className="w-3 h-3 text-slate-200 flex-shrink-0" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Main Content - Horizontal layout with integrated action */}
        <div className="flex items-center justify-between gap-4 py-2">
          {/* Left: Message & Sub-message */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {phaseResult.urgency === 'urgent' && (
                <AlertCircle className={`w-4 h-4 flex-shrink-0 ${styles.icon}`} />
              )}
              {phaseResult.urgency === 'attention' && (
                <Clock className={`w-4 h-4 flex-shrink-0 ${styles.icon}`} />
              )}
              <h2 className="text-base font-semibold text-slate-800 truncate sm:text-lg">
                {phaseResult.message}
              </h2>
            </div>
            
            {/* Sub-message - single line */}
            {phaseResult.subMessage && (
              <p className="text-slate-500 text-sm mt-0.5 line-clamp-2 sm:line-clamp-1">
                {phaseResult.subMessage}
              </p>
            )}
            
            {/* Meeting countdown badge - inline */}
            {phaseResult.phase !== 'file' && phaseResult.daysUntilHearing !== undefined && phaseResult.daysUntilHearing >= 0 && (
              <span className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded text-[10px] font-medium ${
                phaseResult.daysUntilHearing <= 3 
                  ? 'bg-red-50 text-red-600' 
                  : 'bg-teal-50 text-teal-600'
              }`}>
                <Calendar className="w-3 h-3" />
                {phaseResult.daysUntilHearing === 0 
                  ? 'Meeting Today!' 
                  : `Meeting in ${phaseResult.daysUntilHearing} day${phaseResult.daysUntilHearing === 1 ? '' : 's'}`
                }
              </span>
            )}
          </div>

          {/* Right: Action Button - prominent */}
          {phaseResult.actionRoute && (
            <button
              onClick={handleActionClick}
              className={`flex-shrink-0 inline-flex items-center gap-2 text-white px-4 py-2.5 sm:px-5 sm:py-3 rounded-lg font-semibold text-sm transition-all ${styles.button}`}
            >
              <span className="whitespace-nowrap">{phaseResult.actionLabel}</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Calendar Strip - Shows key Texas property tax dates */}
        <CalendarStrip 
          noticeDate={data.noticeDate}
          hearingDate={data.hearingDate}
          protestFiled={data.protestFiled}
        />
      </div>
    </div>
  );
};

export default AppealTimeline;
